import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, SideBarDef } from 'ag-grid-community';
import { CurrencyPipe, Location, NgIf } from '@angular/common';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	FormsModule,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ECLService } from '@lmc-app/core/services/ecl.service';
import { Subject, Subscription } from 'rxjs';

import 'ag-grid-enterprise';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatSuffix,
} from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
	selector: 'app-admin-report-loans',
	templateUrl: './admin-report-loans.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./admin-report-loans.component.scss'],
	imports: [
		MatButton,
		MatFormField,
		MatLabel,
		MatInput,
		ReactiveFormsModule,
		FormsModule,
		NgIf,
		MatIcon,
		MatSuffix,
		MatIconButton,
		AgGridAngular,
	],
})
export class AdminReportLoansComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly _onDestroy = new Subject<void>();

	protected inputDate: string = '2025-12-31'; // For Edit ngOnInit() to get the inputDate
	protected stage: string = '1'; // For Edit ngOnInit() to get the stage ("all" for every stage)
	protected risk: string = 'all'; // For Edit ngOnInit() to get the risk ("all" for every risk)

	protected readonly inputDateForm = new FormGroup({
		input_date: new FormControl(null),
	});

	public readonly headerStyle: any = {
		display: 'flex',
		'justify-content': 'space-between',
	};

	public columnDefs: ColDef[] = [
		{
			field: 'stage',
			headerName: 'Stage',
			width: 90,
		},
		{
			field: 'risk',
			headerName: 'Risk',
			width: 90,
		},
		{
			field: 'loanNumber',
			headerName: 'Loan Number',
			width: 130,
		},
		{
			field: 'accountName',
			headerName: 'Account Name',
			width: 210,
		},
		{
			field: 'loan_group',
			headerName: 'Loan Group',
			width: 100,
		},
		{
			headerName: 'Maturity Date',
			width: 120,
			valueGetter: (params) => params.data.maturityDate.substring(0, 10),
		},
		{
			field: 'status_code',
			headerName: 'Status Code',
			width: 100,
		},
		{
			field: 'interest_rate',
			headerName: 'Interest Rate',
			width: 100,
			valueGetter: (params) => `${params.data.interest_rate}%`,
		},
		{
			headerName: 'Original Loan Amount',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.loanAmount,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Trial Principal Balance',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.principal_balance,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			field: 'ecl',
			headerName: 'ECL Balance',
			width: 150,
			valueFormatter: (params) =>
				this.currencyFormatter.transform(
					params.value,
					'CAD',
					'symbol-narrow'
				) ?? '',
		},
		{
			headerName: 'Trial Principal Date',
			width: 120,
			valueGetter: (params) => params.data.trial_balance_date.substring(0, 10),
		},
		{
			headerName: 'Total Past Due Balance',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.total_past_due_balance,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Interest Balance',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.interest_balance,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Insurance Premium',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.insurance_premium,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Insurance Tax',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.insurance_tax,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			field: 'paymentFrequency',
			headerName: 'Payment Frequency',
			width: 150,
		},
		{
			headerName: 'Closed Date',
			width: 120,
			valueGetter: (params) => params.data.closed_date?.substring(0, 10),
		},
		{
			headerName: 'Reg Payment',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.paymentAmount,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Open Date',
			width: 120,
			valueGetter: (params) => params.data.originationDate.substring(0, 10),
		},
		{
			field: 'days_past_due',
			headerName: 'Days Past Due',
			width: 150,
		},
	];

	protected rowData: { status: string }[] = [];

	protected sideBar: SideBarDef | string | string[] | boolean | null;

	public constructor(
		private _location: Location,
		private router: ActivatedRoute,
		private eclService: ECLService,
		private readonly currencyFormatter: CurrencyPipe
	) {}

	public ngOnInit(): void {
		this.inputDate =
			this.router.snapshot?.paramMap.get('input_date') || '2025-12-31'; // This will retrieve the ID that is linked on the routerlink from the html
		this.stage = this.router.snapshot?.paramMap.get('stage') || 'all'; // This will retrieve the ID that is linked on the routerlink from the html
		this.risk = this.router.snapshot?.paramMap.get('risk') || 'all'; // This will retrieve the ID that is linked on the routerlink from the html
		this.getFundingList(this.inputDate, this.stage, this.risk);
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected backClicked() {
		this._location.back();
	}

	private getFundingList(inputDate: string, stage: string, risk: string) {
		this.parentSub.add(
			this.eclService.getLoans(inputDate, stage, risk).subscribe((res: any) => {
				this.rowData = res;
			})
		);
	}

	private gridApi: GridApi<any>;

	protected filterValue: string = '';
	protected tooltipShowDelay = 0;
	protected tooltipHideDelay = 2000;

	protected overlayLoadingTemplate =
		'<span class="ag-overlay-loading-center">Loading Data...</span>';
	protected overlayNoRowsTemplate =
		'<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">This is a custom \'no rows\' overlay</span>';

	protected defaultColDef: ColDef = {
		sortable: true,
		resizable: true,
		filter: true,
		wrapHeaderText: true,
		autoHeaderHeight: true,
	};

	protected onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridApi.showLoadingOverlay();
	}

	protected onFilterTextBoxChanged() {
		this.gridApi.setQuickFilter(this.filterValue);
	}

	protected onFilterClear() {
		this.filterValue = '';
		this.onFilterTextBoxChanged();
	}

	protected resetFilter() {
		this.gridApi?.setFilterModel(null);
		this.onFilterClear();
	}
}
